import type { Column } from '../../claim-history/simple-table'

import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useCallback, useMemo, useReducer } from 'react'
import { MdMail } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import { useAppDispatch, useAppSelector } from '../../../store'
import * as Table from '../../claim-history/simple-table'
import { Copyable } from '../../layout/sidebar'
import { ContextMenuButton } from '../../shared/ContextMenu'
import { Button, Modal } from '../../shared/NarusMaterial'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface Enrollment extends Record<string, unknown> {
    id: string
    status: EnrollmentStatus
    author_id: string
    subject_id: string
    document: EnrollmentDocument
    client_id: string
    org_id: string
    phone_confirmed: boolean
    email_confirmed: boolean
    created_at: string
    updated_at: string
    email_sent: string
    sms_sent: string | null
    enrollment_type: EnrollmentType
    enrollment_type_display: string
    last_name: string
    first_name: string
    email: string
    phone: string | null
    client_name: string
    link: string
    registerEmail: string
    email_expired: string
    sms_expired: string | null
}

interface EnrollmentDocument {
    pin: string
    role: string
    email: string
    phone: string
    domain: string
    userId: string
    lastName: string
    firstName: string
    patientId: string
    clientName: string
    resourceId: string
    secretCode: string
    initiatedBy?: Resource
}

interface Resource {
    resourceId: string
    meta: ResourceMeta
}

interface ResourceMeta {
    given: string
    family: string
    suffix: string[]
}

type QueryKey = ['enrollments', SearchParams]

interface QueryData {
    data: Enrollment[]
    total: number
    count: number
}

const enum EnrollmentType {
    SelfEnrollment = 'self_enrollment',
    ReferredEnrollment = 'referred_enrollment'
}

let enrollmentTypeMapper: Record<EnrollmentType, string> = {
    [EnrollmentType.SelfEnrollment]: 'Self Enrollment',
    [EnrollmentType.ReferredEnrollment]: 'Referred Enrollment'
}

const enum EnrollmentStatus {
    Finished = 'finished',
    Started = 'started'
}

let enrollmentStatusMapper: Record<EnrollmentStatus, string> = {
    [EnrollmentStatus.Finished]: 'Finished',
    [EnrollmentStatus.Started]: 'Started'
}

interface Client {
    key: string
    display: string
    group_id: string
    entity: string
}

interface State {
    searchParams: SearchParams
    previewedEnrollment?: Pick<Enrollment, 'id' | 'registerEmail'>
}

type Action =
    | { type: 'query/set'; payload: string }
    | { type: 'filters/set'; payload: SearchParams['filters'] }
    | { type: 'filters/merge'; payload: Partial<SearchParams['filters']> }
    | { type: 'filters/clear' }
    | { type: 'sort/set'; payload: Sort }
    | { type: 'pagination/page/set'; payload: number }
    | { type: 'pagination/page-size/set'; payload: number }
    | { type: 'preview/set'; payload: State['previewedEnrollment'] }
    | { type: 'preview/clear' }

interface SearchParams {
    query: string

    filters: {
        status: EnrollmentStatus[]
        enrollment_type: EnrollmentType[]
        client_id: string[]
    }

    sort: Sort
    pagination: Pagination
}

let sortKeys = [
    'created_at',
    'updated_at',
    'name',
    'email',
    'phone',
    'enrollment_type',
    'status',
    'client_name'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
    field: SortKey
    direction: SortDirection
}

interface Pagination {
    page: number
    pageSize: number
}

let initialState: State = {
    searchParams: {
        query: '',
        filters: {
            status: [],
            enrollment_type: [],
            client_id: []
        },
        sort: {
            field: 'created_at',
            direction: 'desc'
        },
        pagination: {
            page: 0,
            pageSize: 10
        }
    }
}

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'query/set': {
            return {
                ...state,
                searchParams: {
                    ...state.searchParams,
                    query: action.payload,
                    pagination: {
                        ...state.searchParams.pagination,
                        page: 0
                    }
                }
            }
        }

        case 'filters/set': {
            return {
                ...state,
                searchParams: {
                    ...state.searchParams,
                    filters: action.payload,
                    pagination: {
                        ...state.searchParams.pagination,
                        page: 0
                    }
                }
            }
        }

        case 'filters/merge': {
            return {
                ...state,
                searchParams: {
                    ...state.searchParams,
                    filters: {
                        ...state.searchParams.filters,
                        ...action.payload
                    },
                    pagination: {
                        ...state.searchParams.pagination,
                        page: 0
                    }
                }
            }
        }

        case 'filters/clear': {
            return {
                ...state,
                searchParams: {
                    ...state.searchParams,
                    query: initialState.searchParams.query,
                    filters: initialState.searchParams.filters,
                    pagination: {
                        ...state.searchParams.pagination,
                        page: 0
                    }
                }
            }
        }

        case 'sort/set': {
            return {
                ...state,
                searchParams: {
                    ...state.searchParams,
                    sort: action.payload,
                    pagination: {
                        ...state.searchParams.pagination,
                        page: 0
                    }
                }
            }
        }

        case 'pagination/page/set': {
            return {
                ...state,
                searchParams: {
                    ...state.searchParams,
                    pagination: {
                        ...state.searchParams.pagination,
                        page: action.payload
                    }
                }
            }
        }

        case 'pagination/page-size/set': {
            return {
                ...state,
                searchParams: {
                    ...state.searchParams,
                    pagination: {
                        ...state.searchParams.pagination,
                        pageSize: action.payload,
                        page: 0
                    }
                }
            }
        }

        case 'preview/set': {
            return {
                ...state,
                previewedEnrollment: action.payload
            }
        }

        case 'preview/clear': {
            return {
                ...state,
                previewedEnrollment: undefined
            }
        }

        default: {
            return state
        }
    }
}

let sortMapper: Record<string, string> = {
    name: 'last_name'
}

let filterMapper: Record<string, string> = {
    client_name: 'client_id'
}

function isDefaultSearchParams(
    searchParams: Pick<SearchParams, 'query' | 'filters'>
): boolean {
    return (
        searchParams.query === initialState.searchParams.query &&
        searchParams.filters.status.length ===
            initialState.searchParams.filters.status.length &&
        searchParams.filters.status.every((status) =>
            initialState.searchParams.filters.status.includes(status)
        ) &&
        searchParams.filters.enrollment_type.length ===
            initialState.searchParams.filters.enrollment_type.length &&
        searchParams.filters.enrollment_type.every((enrollmentType) =>
            initialState.searchParams.filters.enrollment_type.includes(
                enrollmentType
            )
        ) &&
        searchParams.filters.client_id.length ===
            initialState.searchParams.filters.client_id.length &&
        searchParams.filters.client_id.every((clientId) =>
            initialState.searchParams.filters.client_id.includes(clientId)
        )
    )
}

export function EnrollmentsPage(): JSX.Element {
    let [state, dispatch] = useReducer(reducer, initialState)
    let [debouncedSearchQuery] = useDebounceValue(state.searchParams.query, 500)

    // @ts-expect-error // It's fine
    let role: string = useAppSelector(
        (state) => state?.auth?.profile?.app_metadata?.roles.meta.role
    )
    let isAdmin = [
        'administrator',
        'executive_director',
        'medical_director'
    ].includes(role)
    let mobileManagement: string = useAppSelector(
        (state) => state?.globalVars?.permissions?.mobile_management ?? 'none'
    )
    let hasAccessToActions = isAdmin || mobileManagement === 'write'

    let appDispatch = useAppDispatch()

    let query = useQuery<unknown, unknown, QueryData, QueryKey>({
        queryKey: [
            'enrollments',
            { ...state.searchParams, query: debouncedSearchQuery }
        ],

        async queryFn({ queryKey }) {
            let [, searchParams] = queryKey
            let { query, filters, sort, pagination } = searchParams
            let normalizedQuery = query.trim().toLowerCase()

            let sp = new URLSearchParams([
                ['count', pagination.pageSize.toString()],
                ['page', pagination.page.toString()],
                ['on', sortMapper[sort.field] ?? sort.field],
                ['dir', sort.direction],
                ...filters.enrollment_type.map((v) => [
                    'filters.enrollment_type',
                    v
                ]),
                ...filters.status.map((v) => ['filters.status', v]),
                ...filters.client_id.map((v) => ['filters.client_id', v]),
                ...(normalizedQuery.length > 0
                    ? [['search', normalizedQuery]]
                    : [])
            ])

            let response = await http.getRequest(
                `/admin/enrollments?${sp.toString()}`
            )
            return response
        },

        placeholderData: keepPreviousData
    })

    let clientsQuery = useQuery<unknown, unknown, { data: Client[] }>({
        queryKey: ['clients'],

        async queryFn() {
            let response = await http.getRequest('/admin/clients?count=9999')
            return response.data
        },

        placeholderData: keepPreviousData
    })

    let columns = useMemo<Array<Column<Enrollment>>>(
        () => [
            {
                name: 'created_at',
                header: 'Time',
                isSortable: true,
                initialSortDirection: 'desc',
                getValue(entry) {
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {Table.formatTime(entry.created_at)}
                        </span>
                    )
                }
            },
            {
                name: 'name',
                header: 'Name',
                isSortable: true,
                getValue(entry) {
                    return [entry.first_name?.trim(), entry.last_name?.trim()]
                        .filter(Boolean)
                        .join(' ')
                }
            },
            {
                name: 'email',
                header: 'Email',
                isSortable: true
            },
            {
                name: 'phone',
                header: 'Phone',
                isSortable: true
            },
            {
                name: 'enrollment_type',
                header: 'Enrollment Type',
                isSortable: true,
                getValue(entry) {
                    return (
                        enrollmentTypeMapper[entry.enrollment_type] ??
                        entry.enrollment_type
                    )
                }
            },
            {
                name: 'status',
                header: 'Status',
                isSortable: true,
                getValue(entry) {
                    return enrollmentStatusMapper[entry.status] ?? entry.status
                }
            },
            {
                name: 'updated_at',
                header: 'Updated',
                isSortable: true,
                initialSortDirection: 'desc',
                getValue(entry) {
                    return (
                        <span style={{ whiteSpace: 'nowrap' }}>
                            {Table.formatTime(entry.updated_at)}
                        </span>
                    )
                }
            },
            {
                name: 'client_name',
                header: 'Client',
                isSortable: true
            },
            {
                name: 'actions',
                header: null,
                style: {
                    flexShrink: 1
                },
                getValue(entry) {
                    return (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end'
                            }}
                        >
                            <ContextMenuButton
                                actions={[
                                    {
                                        id: 'show-enrollment-email',
                                        icon: MdMail,
                                        label: 'Show enrollment email',
                                        onClick: () => {
                                            dispatch({
                                                type: 'preview/set',
                                                payload: entry
                                            })
                                        }
                                    }
                                ]}
                                onClose={() => ({})}
                            />
                        </div>
                    )
                }
            }
        ],
        [appDispatch, hasAccessToActions]
    )

    let filters = useMemo(() => {
        return [
            {
                name: 'enrollment_type',
                value: state.searchParams.filters.enrollment_type,
                options: [
                    {
                        value: EnrollmentType.SelfEnrollment,
                        label: enrollmentTypeMapper[
                            EnrollmentType.SelfEnrollment
                        ]
                    },
                    {
                        value: EnrollmentType.ReferredEnrollment,
                        label: enrollmentTypeMapper[
                            EnrollmentType.ReferredEnrollment
                        ]
                    }
                ]
            },
            {
                name: 'status',
                value: state.searchParams.filters.status,
                options: [
                    {
                        value: EnrollmentStatus.Started,
                        label: enrollmentStatusMapper[EnrollmentStatus.Started]
                    },
                    {
                        value: EnrollmentStatus.Finished,
                        label: enrollmentStatusMapper[EnrollmentStatus.Finished]
                    }
                ]
            },
            ...(Array.isArray(clientsQuery.data) && clientsQuery.data.length > 0
                ? [
                      {
                          name: 'client_name',
                          value: state.searchParams.filters.client_id,
                          options: clientsQuery.data.map((client) => ({
                              value: client.key,
                              label: client.display
                          }))
                      }
                  ]
                : [])
        ]
    }, [state.searchParams.filters, clientsQuery.data])

    let handleFilterChange = useCallback(
        <T extends keyof Enrollment>(
            name: T,
            value: Array<Enrollment[T]>
        ): void => {
            let mappedName = filterMapper[name as string] ?? name

            dispatch({
                type: 'filters/merge',
                payload: {
                    [mappedName]: value
                }
            })
        },
        []
    )

    let handleFiltersClear = useCallback((): void => {
        dispatch({
            type: 'filters/clear'
        })
    }, [])

    let handleSortChange = useCallback(
        (field: keyof Enrollment, direction: SortDirection): void => {
            function isSortKey(key: unknown): key is SortKey {
                return (sortKeys as readonly unknown[]).includes(key)
            }

            if (!isSortKey(field)) {
                return
            }

            dispatch({
                type: 'sort/set',
                payload: {
                    field,
                    direction
                }
            })
        },
        []
    )

    let handlePageChange = useCallback((value: number): void => {
        dispatch({
            type: 'pagination/page/set',
            payload: value - 1
        })
    }, [])

    let handlePageSizeChange = useCallback((value: number): void => {
        dispatch({
            type: 'pagination/page-size/set',
            payload: value
        })
    }, [])

    let pagination = useMemo(() => {
        return {
            ...state.searchParams.pagination,
            totalFiltered: query.data?.total ?? 0,
            totalUnfiltered: query.data?.total ?? 0
        }
    }, [state.searchParams.pagination, query.data])

    return (
        <div>
            <PageHeader className={styles.pageHeader}>
                <h1>
                    <span>Enrollments</span>
                </h1>

                <SearchInput
                    value={state.searchParams.query}
                    placeholder="Search by name, email, phone, enrollment type, client name and status"
                    onChange={(event) => {
                        dispatch({
                            type: 'query/set',
                            payload: event.target.value
                        })
                    }}
                />
            </PageHeader>

            <Table.Table
                data={query.data?.data ?? []}
                queryStatus={query.status}
                noDataPlaceholder="No enrollments found"
                forceClearFilters={!isDefaultSearchParams(state.searchParams)}
                columns={columns}
                filters={filters}
                sort={state.searchParams.sort}
                pagination={pagination}
                onFilterChange={handleFilterChange}
                onFiltersClear={handleFiltersClear}
                onSortChange={handleSortChange}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                expandingComponent={(item, state, setState) => {
                    return <ExpandingComponent item={item} />
                }}
            />

            {state.previewedEnrollment && (
                <Modal
                    title="Enrollment Email Preview"
                    actions={[
                        <Button
                            key={1}
                            onClick={() => {
                                dispatch({ type: 'preview/clear' })
                            }}
                            label="Close"
                        />
                    ]}
                    onClose={() => {
                        dispatch({ type: 'preview/clear' })
                    }}
                >
                    <div
                        dangerouslySetInnerHTML={{
                            __html: state.previewedEnrollment.registerEmail
                        }}
                    />
                </Modal>
            )}
        </div>
    )
}

interface ExpandingComponentProps {
    item: Enrollment
}

function ExpandingComponent({ item }: ExpandingComponentProps): JSX.Element {
    return (
        <div className={styles.row}>
            <div className={styles.details}>
                <span className={styles.label}>Time</span>
                <span className={styles.value}>
                    {Table.formatTime(item.created_at)}
                </span>

                <span className={styles.label}>Patient</span>
                <span className={styles.value}>
                    <Link
                        to={`/patient/${item.client_id}/${
                            item.subject_id?.split('/')[1] ?? ''
                        }/patientFolder`}
                    >
                        {[item.document.firstName, item.document.lastName]
                            .filter(Boolean)
                            .join(' ')}
                    </Link>
                </span>

                <span className={styles.label}>Email</span>
                <span className={styles.value}>
                    {typeof item.email === 'string' && (
                        <Copyable value={item.email}>{item.email}</Copyable>
                    )}

                    {typeof item.email !== 'string' && '—'}
                </span>

                <span className={styles.label}>Phone</span>
                <span className={styles.value}>
                    {(item.phone ?? '') || '—'}
                </span>

                <span className={styles.label}>Enrollment Type</span>
                <span className={styles.value}>
                    {enrollmentTypeMapper[item.enrollment_type] ||
                        item.enrollment_type}
                </span>

                <span className={styles.label}>Status</span>
                <span className={styles.value}>
                    {enrollmentStatusMapper[item.status] || item.status}
                </span>

                <span className={styles.label}>Updated at</span>
                <span className={styles.value}>
                    {Table.formatTime(item.updated_at)}
                </span>

                <span className={styles.label}>Client</span>
                <span className={styles.value}>{item.client_name || '—'}</span>

                <span className={styles.label}>Initiated by</span>
                <span className={styles.value}>
                    {[
                        item.document.initiatedBy?.meta.given,
                        item.document.initiatedBy?.meta.family
                    ]
                        .filter(Boolean)
                        .join(' ') || '—'}
                </span>
            </div>

            <div className={styles.details}>
                <span className={styles.label}>Enrollment Code</span>
                <span className={styles.value}>
                    {typeof item.id === 'string' && (
                        <Copyable value={item.id}>{item.id}</Copyable>
                    )}

                    {typeof item.id !== 'string' && '—'}
                </span>

                <span className={styles.label}>Email Code</span>
                <span className={styles.value}>
                    {typeof item.document.secretCode === 'string' && (
                        <Copyable value={item.document.secretCode}>
                            {item.document.secretCode}
                        </Copyable>
                    )}

                    {typeof item.document.secretCode !== 'string' && '—'}
                </span>

                <span className={styles.label}>Email Sent</span>
                <span className={styles.value}>
                    {item.email_sent && Table.formatTime(item.email_sent)}
                    {!item.email_sent && '—'}
                </span>

                <span className={styles.label}>Email Expired</span>
                <span className={styles.value}>
                    {item.email_expired && Table.formatTime(item.email_expired)}
                    {!item.email_expired && '—'}
                </span>

                <span className={styles.label}>SMS Code</span>
                <span className={styles.value}>
                    {typeof item.document.pin === 'string' && (
                        <Copyable value={item.document.pin}>
                            {item.document.pin}
                        </Copyable>
                    )}

                    {typeof item.document.pin !== 'string' && '—'}
                </span>

                <span className={styles.label}>SMS Sent</span>
                <span className={styles.value}>
                    {item.sms_sent && Table.formatTime(item.sms_sent)}
                    {!item.sms_sent && '—'}
                </span>

                <span className={styles.label}>SMS Expired</span>
                <span className={styles.value}>
                    {item.sms_expired && Table.formatTime(item.sms_expired)}
                    {!item.sms_expired && '—'}
                </span>

                <span className={styles.label}>Dynamic Link</span>
                <div
                    style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    <span className={styles.value}>
                        <a href={item.link} target="_blank" rel="noreferrer">
                            {item.link}
                        </a>
                    </span>
                </div>
            </div>
        </div>
    )
}
